import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../components/Spin";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import { Input } from "../../../components/ui/input";
import { API, fileUpload, get } from "../../../config";
import { PhoneInput } from "../../../components/ui/phone-input";
import SearchSelect from "../../../components/SearchSelect";
import { useRoleList } from "../../../lib/OptionsAPI";
import { Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import { AddAdminUserSchema } from "../../../lib/Validation";

const AddEditUser = ({
  id,
  toggleShowAddEditUser,
  edit,
  fetchUserData,
  orgId,
}) => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { organizationData } = useOrganization();

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.GET_ADMIN_USER_LIST}/` + id);
      setUserData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setUserData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Role
  // Here is for the admin roles
  const { roleLoading, roleList = [] } = useRoleList(true);
  const roleListing = roleList ? roleList : [];

  const orgID = organizationData?.organizationList?.find(
    (item) => item?.value === userData?.org_id
  )?.value;

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append("first_name", value?.first_name ? value?.first_name : "");
    formData.append("last_name", value?.last_name ? value?.last_name : "");
    formData.append("mobile", value?.mobile ? value?.mobile : "");
    formData.append("email", value?.email ? value?.email : "");
    formData.append("role_id", value?.role_id ? value?.role_id : "");

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.GET_ADMIN_USER_LIST}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditUser();
        fetchUserData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        org_Id: orgID ? [orgID] : [orgId],
        first_name: userData?.first_name,
        last_name: userData?.last_name,
        mobile: userData?.mobile,
        email: userData?.email,
        office_id: userData?.office_id ? userData?.office_id : "",
        role_id: userData?.role_id ? userData?.role_id : "",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminUserSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin fullscreen={true}/>}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="first_name">
                      <CustomLabelText
                        required={false}
                        labelText="First Name"
                        errorField={errors?.first_name}
                      />
                    </Label>
                    <Input
                      id="first_name"
                      type="text"
                      name="first_name"
                      value={values?.first_name ? values?.first_name : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.first_name &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="last_name">
                      <CustomLabelText
                        required={true}
                        labelText="Last Name"
                        errorField={errors?.last_name}
                      />
                    </Label>
                    <Input
                      id="last_name"
                      type="text"
                      name="last_name"
                      value={values?.last_name ? values?.last_name : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.last_name &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="email">
                      <CustomLabelText
                        required={true}
                        labelText="Email"
                        errorField={errors?.email}
                      />
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="m@example.com"
                      required
                      autoComplete="off"
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values?.email}
                      className={
                        errors?.email &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="phone">
                      <CustomLabelText
                        required={false}
                        labelText="Mobile No."
                        errorField={errors?.mobile}
                      />
                    </Label>
                    <PhoneInput
                      onChange={(e) => {
                        setFieldValue("mobile", e);
                      }}
                      value={values?.mobile}
                      defaultValue={"+61"}
                      defaultCountry={"AU"}
                      minLength={15}
                      maxLength={15}
                      onBlur={handleBlur}
                      className={
                        errors?.mobile &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="role_id">
                      <CustomLabelText
                        required={true}
                        labelText="Role"
                        errorField={errors?.role_id}
                      />
                    </Label>

                    <div className="relative">
                      <SearchSelect
                        frameworks={roleListing}
                        optionType={"Role"}
                        handleSelect={(e) => {
                          setFieldValue("role_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.role_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={false}
                        initialValue={values?.role_id}
                      />
                      {roleLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditUser();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditUser;
